import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, Mail, Lock, User as UserIcon, AlertCircle } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

type Mode = 'signin' | 'signup';

export default function LoginPage() {
  const { user, isAuthenticating, error, signInWithGoogle, signInWithEmail, signUpWithEmail, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');

  useEffect(() => {
    if (user) {
      const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;
      navigate(from || '/dashboard', { replace: true });
    }
  }, [user, navigate, location.state]);

  useEffect(() => {
    if (error) clearError();
  }, [mode, email, password, displayName, clearError, error]);

  async function handleGoogle() {
    try { await signInWithGoogle(); } catch { /* handled */ }
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (mode === 'signin') await signInWithEmail(email, password);
      else await signUpWithEmail(email, password, displayName);
    } catch { /* handled */ }
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-sky-50 to-slate-100 px-4 py-8">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-6">
          <img src="/kejja.svg" alt="KEJJA" className="h-24 md:h-28 w-auto object-contain" />
        </div>

        <Card className="border border-slate-200 shadow-xl bg-white">
          <CardContent className="px-6 py-7">
            <div className="text-center mb-5">
              <h1 className="text-xl font-semibold text-[#0e415e]">
                {mode === 'signin' ? 'Welcome home.' : 'Create your account'}
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                {mode === 'signin'
                  ? 'Sign in to continue to KEJJA.'
                  : 'Join KEJJA — the family operating system.'}
              </p>
            </div>

            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2.5 mb-4 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="button"
              onClick={handleGoogle}
              disabled={isAuthenticating}
              className="w-full h-10 flex items-center justify-center gap-2.5 rounded-md border border-slate-300 bg-white text-slate-700 font-medium hover:bg-slate-50 active:bg-slate-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {isAuthenticating ? <Loader2 className="w-4 h-4 animate-spin" /> : <GoogleIcon />}
              <span>Continue with Google</span>
            </button>

            <div className="flex items-center gap-3 my-4">
              <Separator className="flex-1" />
              <span className="text-[11px] text-slate-500 uppercase tracking-widest">or</span>
              <Separator className="flex-1" />
            </div>

            <form onSubmit={handleEmailSubmit} className="space-y-2.5">
              {mode === 'signup' && (
                <div className="relative">
                  <UserIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text" required placeholder="Your name"
                    className="w-full h-10 pl-10 pr-3 rounded-md border border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus:border-[#196287] focus:ring-2 focus:ring-[#196287]/20 outline-none transition"
                    value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                  />
                </div>
              )}

              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email" required placeholder="Email address" autoComplete="email"
                  className="w-full h-10 pl-10 pr-3 rounded-md border border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus:border-[#196287] focus:ring-2 focus:ring-[#196287]/20 outline-none transition"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="password" required minLength={6}
                  placeholder={mode === 'signup' ? 'Choose a password (6+ chars)' : 'Password'}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  className="w-full h-10 pl-10 pr-3 rounded-md border border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus:border-[#196287] focus:ring-2 focus:ring-[#196287]/20 outline-none transition"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <Button
                type="submit"
                className="w-full h-10 font-semibold bg-[#196287] hover:bg-[#0e415e] text-white mt-1"
                disabled={isAuthenticating}
              >
                {isAuthenticating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {mode === 'signin' ? 'Sign in' : 'Create account'}
              </Button>
            </form>

            <p className="text-center text-sm text-slate-600 mt-4">
              {mode === 'signin' ? (
                <>
                  Don't have an account?{' '}
                  <button type="button" onClick={() => setMode('signup')}
                    className="text-[#196287] hover:text-[#0e415e] font-medium underline-offset-2 hover:underline">
                    Create one
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button type="button" onClick={() => setMode('signin')}
                    className="text-[#196287] hover:text-[#0e415e] font-medium underline-offset-2 hover:underline">
                    Sign in
                  </button>
                </>
              )}
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-6 tracking-wide">
          Built for families. Built for Africa. Built to last.
        </p>
        <p className="text-center text-[11px] text-slate-400 mt-1 tracking-wide">
          kejja.saidalink.tech
        </p>
      </div>
    </main>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.7 4.7-6.2 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.1 0-9.4-3.3-11.1-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.5-4.4 5.9l6.2 5.2C42 35 44 29.9 44 24c0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}
